import {
  memo,
  type FC,
} from 'react';
import {
  styled,
  Link,
  Sheet,
  Stack,
  Typography,
} from '@mui/joy';
import {
  isEmpty
} from '@shared/lib';
import {
  Button
} from '@shared/ui';
import {
  CoinPackageVariantEnum,
  type CoinPackage,
} from '../lib';
import {
  ChosenCoinPackageSummary
} from './chosen-coin-package-summary.component';
import {
  ChosenCoinPackageTitle
} from './chosen-coin-package-card-title.component';

const CardContainer = styled(Stack)<{ darkMode?: boolean; viewMode?: boolean; shortMode: boolean; }>(({ theme, darkMode, viewMode, shortMode }) => ({
  height: 101,
  width: '100%',
  paddingTop: 37,
  borderRadius: 8,
  paddingInline: 16,
  cursor: 'pointer',
  overflow: 'hidden',
  position: 'relative',
  justifyContent: 'flex-end',
  paddingBottom: 7,
  background: theme.palette.common[475],
  ...(shortMode && ({
    height: '100%',
    paddingTop: 16,
  })),
  ...(viewMode && ({ paddingBottom: 16 })),
  ...(darkMode && ({ background: theme.palette.common[900] })),
}));

const CardContent = styled(Sheet)(() => ({
  gap: '5px',
  display: 'grid',
  background: 'transparent',
  gridTemplateColumns: '1fr fit-content(100%)',
}));

export type ChosenCoinPackageCardProps = {
  data: CoinPackage;
  viewMode?: boolean;
  darkMode?: boolean;
  isClaimed?: boolean;
  onPurchase?(coinPackage: CoinPackage): void;
};

export const ChosenCoinPackageCard: FC<ChosenCoinPackageCardProps> = memo(({
  data,
  viewMode = false,
  darkMode = false,
  onPurchase,
}) => {

  const colorByVariant = data?.variant === CoinPackageVariantEnum.MostPopular
    ? 'success'
    : 'primary';

  const handlePurchase = (): void => {
    onPurchase?.(data);
  };

  return (
    <CardContainer
      viewMode={viewMode}
      darkMode={darkMode}
      shortMode={isEmpty(data?.title)}
      onClick={handlePurchase}>
      <ChosenCoinPackageTitle
        text={data?.title}
        variant={data?.variant}
      />
      <CardContent>
        <ChosenCoinPackageSummary
          variant={data.variant}
          redeemableCoinAmount={data.redeemableCoinAmount}
          nonRedeemableCoinAmount={data.nonRedeemableCoinAmount}
        />
        {viewMode ? (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='flex-end'>
            <Typography
              justifyContent='center'
              sx={({ palette }) => ({
                fontSize: 14,
                fontWeight: 700,
                lineHeight: '20px',
                color: palette.common.white,
              })}>
              {`Total: ${data.currency} ${data.price}`}
            </Typography>
          </Stack>
        ) : (
          <Stack
            direction='column'
            alignItems='center'
            justifyContent='space-around'>
            <Button
              size='sm'
              color={colorByVariant}
              sx={{ width: 151, height: 37 }}>
              {`${data.currency} ${data.price} Claim Now`}
            </Button>
            {data.promoTAndCApplyUrl && (
              <Link
                justifyContent='center'
                color='neutral'
                target='_blank'
                underline='none'
                href={data.promoTAndCApplyUrl}
                onClick={e => e.stopPropagation()}
                sx={{
                  fontSize: 12,
                  lineHeight: '20px',
                }}>
                Promo T&Cs Apply
              </Link>
            )}
          </Stack>
        )}
      </CardContent>
    </CardContainer>
  );
});